import {
  useState,
  useEffect,
  JSXElementConstructor,
  ReactElement,
  ReactFragment,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  DataGrid,
  GridColDef,
  GridColumnVisibilityModel,
  jaJP,
} from "@mui/x-data-grid";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import UpdateButton from "../../atoms/Button/UpdateButton";
import {
  CustomContainer,
  StyledDataGrid,
  customDataGridLocaleTextAll,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
} from "../../../styles/styles";
import IconButtonWithTooltip from "../../atoms/Button/IconButtonWithTooltip";
import EditIcon from "@mui/icons-material/Edit";
import { formatDate } from "../../../common/const";

interface Building {
  autoBuildingId: number;
  buildingId: string;
  buildingName: string;
  buildingAddress: string;
  createdAt: string;
  sdcServiceName: string;
  memo: string;
  isEnabled: boolean;
  ownerName: string;
  fulltechOffice: {
    autoFulltechOfficeId: number;
    officeName: string;
  };
  owner: {
    autoOwnerId: number;
    ownerName: string;
  };
}

const BuildingListPage = () => {
  const navigate = useNavigate();

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      buildingId: false, // 物件システムIDを非表示
    });

  // useStateフックの初期値を空の配列に設定
  const [data, setData] = useState<Building[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  // useEffectフック内のロジックを修正
  useEffect(() => {
    const fetchData = async () => {
      // JWTトークンを取得します
      const jwtToken = await getJwtTokenFromIdToken();
      try {
        // call indexBuildings API
        const response = await fetch(
          process.env.REACT_APP_API_ENDPOINT + "/buildings",
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: jwtToken,
            },
          }
        );
        const jsonData = await response.json();
        // レスポンスが正常であればjsonData.buildingsをsetDataで設定し、
        // エラーが発生した場合は空の配列をsetDataで設定
        setData(jsonData.buildings || []);
        setIsLoading(false);
        console.log("取得リストデータ" + JSON.stringify(jsonData));
      } catch (error: any) {
        setError(error);
        setIsLoading(false);
        // エラーが発生したときも空の配列をsetDataで設定
        setData([]);
      }
    };

    fetchData();
  }, []);

  const handleEditBuildingClick = (autoBuildingId: number) => {
    navigate(`/buildings/${autoBuildingId}`);
    // navigate(`/buildings/edit`);
    console.log(
      `物件編集 button clicked` + "　選択された物件" + autoBuildingId
    );
  };

  const handleNewBuildingClick = () => {
    navigate(`/buildings/new`);
    console.log(`物件新規登録 button clicked`);
  };

  if (isLoading) {
    <Box display="flex" justifyContent="center" alignItems="center">
      <CircularProgress /> {/* MUIの CircularProgress コンポーネント */}
    </Box>;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const columns: GridColDef[] = [
    // { field: "isEnabled", headerName: "状態", flex: 0.5, valueGetter: (params: any) => params.row.isEnabled ? "有効" : "無効", renderCell: (params: any) => (<Tooltip title={params.value}><span>{params.value}</span></Tooltip>) },
    {
      field: "buildingId",
      headerName: "物件システムID",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "buildingManagementId",
      headerName: "取付先コード",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "officeName",
      headerName: "管轄拠点",
      flex: 1,
      valueGetter: (params) => params.row.fulltechOffice.officeName,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "createdAt",
      headerName: "登録日",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value ? formatDate(params.value) : ""}>
          <span>{params.value ? formatDate(params.value) : ""}</span>
        </Tooltip>
      ),
      valueFormatter: (params: any) => {
        // 日付が存在する場合はformatDate関数を利用してフォーマット
        return params.value ? formatDate(params.value) : "";
      },
    },    
    {
      field: "buildingName",
      headerName: "取付先名（物件名称）",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "buildingAddress",
      headerName: "住所",
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "ownerName",
      headerName: "LAD契約者（オーナー）名称",
      flex: 1,
      valueGetter: (params) => params.row.owner?.ownerName,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "memo",
      headerName: "メモ",
      flex: 2,
      valueGetter: (params) => params.row?.memo,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "autoBuildingId",
      headerName: "操作",
      flex: 1,
      renderCell: (params) => (
        <IconButtonWithTooltip
          title="編集"
          onClick={() => handleEditBuildingClick(params.row.autoBuildingId)}
          icon={<EditIcon />}
        />
      ),
    },
  ];
  return (
    <CustomContainer maxWidth={false}>
      <Box display="flex" flexDirection="column" marginBottom={2}>
        <Typography variant="h5" component="h2" gutterBottom>
          管理物件一覧
        </Typography>
        <Box display="flex" justifyContent="flex-end">
          <Box marginLeft="auto">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleNewBuildingClick}
            >
              物件新規登録
            </Button>
          </Box>
          <Box marginLeft={1}>
            <UpdateButton />
          </Box>
        </Box>
      </Box>
      <div style={{ height: 700, width: "100%" }}>
        <StyledDataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row.autoBuildingId}
          disableRowSelectionOnClick
          localeText={
            isLoading
              ? customDataGridLocaleTextLoading
              : data.length === 0
              ? customDataGridLocaleTextNoData
              : customDataGridLocaleTextAll
          }
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) =>
            setColumnVisibilityModel(newModel)
          }
        />
      </div>
    </CustomContainer>
  );
};

export default BuildingListPage;
